/**
 * A component for collecting, uploading, and managing document files.
 * Supports multiple document types with file upload and deletion functionality.
 */
import React, { useState, useCallback, useMemo } from "react";
import { Button, Flex } from "@jsq/ds/core";
import { useFormContext, Form } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { DocumentFormValues } from "../../../hooks/documents/useDocumentProcessor";
import { DefaultModal } from "../../common/DefaultModal";
import { SingleFileUpload } from "../../common/SingleFileUpload";
import { DocumentCollectorProps, DocumentFormProps, DocumentData } from "./types";
import { prepareFilesToUpload } from "./utils";

// Supported file formats that can be uploaded through the document collector
const SUPPORTED_FORMATS = ["image/*", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".csv", ".zip"];

/**
 * Renders the form fields for document collection with file upload controls
 * for each document type specified.
 *
 * @param documentDetails - Array of document specifications to collect
 * @param isSubmitting - Whether the form is currently submitting
 * @param onNavigateBack - Optional callback for navigation to previous screen
 */
const DocumentsForm: React.FC<{
  documentDetails: DocumentFormProps[];
  isSubmitting: boolean;
  onNavigateBack?: () => void;
}> = ({ documentDetails, isSubmitting, onNavigateBack }) => {
  const formContext = useFormContext<DocumentFormValues>();

  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      {documentDetails.map((data, index) => (
        <Flex key={index} flexDirection="column">
          <SingleFileUpload
            title={data.title}
            description={data.description}
            initialFile={data.file}
            acceptedFileFormats={SUPPORTED_FORMATS}
            onFileSelected={(file) => {
              void formContext.setFieldValue(`documents[${index}].file`, file);
              return;
            }}
            onFileDeleted={() => {
              void formContext.setFieldValue(`documents[${index}].file`, null);
              return;
            }}
          />
        </Flex>
      ))}
      <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
        {onNavigateBack && (
          <Button variant="neutral" onClick={onNavigateBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting}>
          Complete
        </Button>
      </Flex>
    </Flex>
  );
};

/**
 * Main component for collecting document files with support for uploading, replacing,
 * and deleting documents. Tracks which files are new, which are replacements, and
 * which have been removed.
 *
 * @param documentDetails - Array of document details to collect
 * @param onNavigateBack - Optional callback for handling navigation to previous screen
 * @param onComplete - Callback for handling form submission with files to delete and upload
 */
export const DocumentCollector: React.FC<DocumentCollectorProps> = ({
  documentDetails,
  isSubmitting,
  onNavigateBack,
  onComplete,
}) => {
  const [documentFormValues, setDocumentFormValues] = useState<
    DocumentFormValues | undefined
  >();

  // Convert DocumentFormProps to DocumentData (extract only essential properties)
  const initialFiles = useMemo(() => {
    return documentDetails.map((detail) => ({
      id: detail.id,
      file: detail.file,
      baseType: detail.baseType,
      subType: detail.subType,
    }));
  }, [documentDetails]);

  const uploadHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      const { replacedFiles, newUploadFiles } = prepareFilesToUpload(
        newSelectedFiles,
        initialFiles,
      );
      onComplete(replacedFiles, newUploadFiles);
    },
    [initialFiles, onComplete],
  );

  const submitHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      const documentsNotAvailable = newSelectedFiles.documents.some(
        (document: DocumentData) => document.file == null,
      );
      if (documentsNotAvailable) {
        setDocumentFormValues(newSelectedFiles);
      } else {
        uploadHandler(newSelectedFiles);
      }
    },
    [uploadHandler],
  );

  return (
    <div>
      <Form<DocumentFormValues>
        initialValues={{
          documents: documentDetails.map((input) => ({
            id: input.id,
            file: input.file,
            baseType: input.baseType,
            subType: input.subType,
          })),
        }}
        submitOnlyChangedValues={false}
        onSubmit={submitHandler}
      >
        <DocumentsForm
          documentDetails={documentDetails}
          isSubmitting={isSubmitting}
          onNavigateBack={onNavigateBack}
        />
      </Form>

      <DefaultModal
        show={!!documentFormValues}
        modalSize="medium"
        title="Missing document warning"
        body="You can continue and sign the subscription, but the subscription cannot be accepted until all required docs are uploaded."
        negBtnText="Cancel"
        onClose={() => setDocumentFormValues(undefined)}
        onConfirm={() => {
          uploadHandler(documentFormValues!);
          setDocumentFormValues(undefined);
        }}
      />
    </div>
  );
};
